/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

import { Breakpoint, useMediaQuery, useTheme } from "@mui/material";
import { ComponentType, FC, type JSX, lazy, Suspense, useMemo } from "react";

export type TLazyComponent<T extends ComponentType<any> = any> = () => Promise<{ default: T }>;

export interface ILazyLoadResponsiveComponentProps<T extends ComponentType<any>> {
  mobileComponent: TLazyComponent<T>;
  webComponent: TLazyComponent<T>;
  loadingComponent?: React.ReactNode;
  mobileBreakpoint?: Breakpoint;
}

const LazyLoadResponsiveComponent: FC<ILazyLoadResponsiveComponentProps<ComponentType<any>>> = <
  T extends ComponentType<any>,
>({
  mobileComponent,
  webComponent,
  loadingComponent,
  mobileBreakpoint,
}: ILazyLoadResponsiveComponentProps<T>): JSX.Element => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(mobileBreakpoint || "md"));

  const Component: React.LazyExoticComponent<any> = useMemo(
    () => (isMobile ? lazy(mobileComponent) : lazy(webComponent)),
    [isMobile]
  );

  return (
    <Suspense fallback={loadingComponent}>
      <Component />
    </Suspense>
  );
};

export default LazyLoadResponsiveComponent;
